import { Button, CircularProgress, Grid, Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import {
  ResponsiveModalActions,
  ResponsiveModalBody,
} from '@src/components/ResponsiveModal/ResponsiveModal'
import { useRequestQuoteForMilliplexMutation } from '@src/mutations/RequestQuoteForMilliplex.generated'
import { Formik, Form, Field, FormikContextType } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { SelectedKits } from '../utils'
import { useLabWaterParticipantId } from '@src/utils/useLabWaterParticipantId'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { milliplexRoute, useRouter } from '@src/routes'
import { MilliplexFormValues } from '../Milliplex'
import {
  validateField,
  isValidEmail,
  isValidPhoneWithSpaces,
} from '@utils/validators'
import * as yup from 'yup'

const useStyles = makeStyles((theme: Theme) => {
  return {
    modalBodyTitleStyle: {
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(3, 2, 2, 4),
      },
    },
    brandPrimary: {
      backgroundColor: theme.palette.secondary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: '#402874',
      },
    },
  }
})

interface QuoteRequestToDealerModalProps {
  open: boolean
  handleClose: () => void
  selectedKits: SelectedKits
  species: string
  researchAreas: string[]
  milliplexFormik: FormikContextType<MilliplexFormValues>
}

const QuoteRequestToDealerModal: React.FC<QuoteRequestToDealerModalProps> = ({
  open,
  handleClose,
  selectedKits,
  species,
  researchAreas,
  milliplexFormik,
}) => {
  const classes = useStyles()
  const intl = useIntl()
  const [requestQuote, { loading }] = useRequestQuoteForMilliplexMutation()
  const participantId = useLabWaterParticipantId()
  const { currentUser } = useCurrentUser()
  const router = useRouter()
  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      renderTitle={() => (
        <Typography
          variant="h2"
          component="h2"
          className={classes.modalBodyTitleStyle}
        >
          <FormattedMessage
            id="QUOTE_REQUEST_TO_DEALER_MODAL_MODAL_TITLE"
            defaultMessage="Quote Request to Dealer"
          />
        </Typography>
      )}
      size={ModalSizes.Large}
    >
      <Formik
        initialValues={{
          emailId: currentUser?.email || '',
          name: '',
          phoneNumber: '',
          comments: '',
        }}
        onSubmit={async (values) => {
          const res = await requestQuote({
            variables: {
              input: {
                participantId,
                emailId: values.emailId,
                name: values.name,
                phoneNumber: values.phoneNumber,
                comments: values.comments,
                selectedAssayList: Object.keys(selectedKits).map((panelId) => ({
                  panelId,
                  panelName: selectedKits[panelId].data.productName,
                  quantity: selectedKits[panelId].data.quantity,
                  species,
                  spaceSaverSelection: selectedKits[panelId].data.spaceSaving,
                  customPremixSelection:
                    selectedKits[panelId].data.customPremixing,
                  sampleType: selectedKits[panelId].data.sampleType,
                  researchAreas,
                  selectedAnalytes: selectedKits[panelId].data.analytesInKit,
                })),
              },
            },
          })

          if (res.data?.requestQuoteForMilliplex) {
            milliplexFormik.resetForm()
            router.push(milliplexRoute.index())
          }
        }}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <ResponsiveModalBody>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <div>
                    <FormattedMessage
                      id="QUOTE_REQUEST_TO_DEALER_MODAL_TEXT"
                      defaultMessage="Please provide your contact information and submit your request to the dealer."
                    />
                  </div>
                  <div>
                    <FormattedMessage
                      id="REQUIRED_FIELDS"
                      defaultMessage="Required Fields"
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={6}>
                    <Grid item xs={12}>
                      <Field
                        name="emailId"
                        label="Email Address"
                        component={LiquidInputAdapter}
                        fullWidth
                        required
                        validate={validateField(
                          yup
                            .string()
                            .trim()
                            .test('validateEmail', 'Invalid Email', (value) =>
                              isValidEmail(value)
                            )
                            .required(
                              intl.formatMessage({
                                id: 'REQUIRE_FIELD',
                                defaultMessage: 'Required Field',
                              })
                            )
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        name="name"
                        label="Full Name"
                        component={LiquidInputAdapter}
                        fullWidth
                        required
                        validate={validateField(
                          yup.string().required(
                            intl.formatMessage({
                              id: 'REQUIRE_FIELD',
                              defaultMessage: 'Required Field',
                            })
                          )
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        name="phoneNumber"
                        label="Phone Number"
                        component={LiquidInputAdapter}
                        fullWidth
                        required
                        validate={validateField(
                          yup
                            .string()
                            .required(
                              intl.formatMessage({
                                id: 'REQUIRE_FIELD',
                                defaultMessage: 'Required Field',
                              })
                            )
                            .test(
                              'validatePhoneWithSpaces',
                              intl.formatMessage({
                                id: 'PHONE_FAX_NO_HELP_MSG',
                                defaultMessage:
                                  'Invalid character entered. Allowed Characters are: Numbers - + ( )',
                              }),
                              (value) => isValidPhoneWithSpaces(value)
                            )
                            .max(
                              20,
                              intl.formatMessage({
                                id: 'MAX_20_CHARACTERS',
                                defaultMessage: 'Must be 20 characters max',
                              })
                            )
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        name="comments"
                        label="Message to Dealer"
                        component={LiquidInputAdapter}
                        fullWidth
                        multiline
                        rows={4}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <ResponsiveModalActions>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    handleClose()
                  }}
                >
                  <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
                </Button>
                <Button
                  className={classes.brandPrimary}
                  variant="contained"
                  size="large"
                  type="submit"
                >
                  <FormattedMessage
                    id="SUBMIT_REQUEST"
                    defaultMessage="Submit Request"
                  />
                  {loading && (
                    <>
                      &nbsp;
                      <CircularProgress
                        color="inherit"
                        size={20}
                        thickness={5}
                      />
                    </>
                  )}
                </Button>
              </ResponsiveModalActions>
            </ResponsiveModalBody>
          </Form>
        )}
      </Formik>
    </ResponsiveModal>
  )
}

export default QuoteRequestToDealerModal
